import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import heroImg1 from "../assets/classroom.png";

import {
  FaArrowRight,
  FaCheck,
  FaWhatsapp,
  FaLaptopCode,
} from "react-icons/fa";

import {
  HiAcademicCap,
  HiBriefcase,
  HiSparkles,
} from "react-icons/hi2";

const HeroSection = () => {
  const shouldReduceMotion = useReducedMotion();

  const trustPoints = [
    "Practical Learning",
    "Real-World Projects",
    "Expert Mentors",
    "Career Support",
  ];

  const learningAreas = [
    "Data Analytics",
    "Full Stack Development",
    "Generative AI",
    "DevOps",
  ];

  const trainingApproach = [
    "Hands-on learning",
    "Real-world projects",
    "Career support",
  ];

  return (
    <section
      aria-labelledby="hero-heading"
      className="
        relative isolate overflow-hidden
        bg-slate-50 text-gray-900
        dark:bg-gray-950 dark:text-white
      "
    >
      {/* =========================================================
          BACKGROUND
      ========================================================= */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0
          -z-20
          overflow-hidden
        "
      >
        {/* Blue atmosphere */}
        <div
          className="
            absolute
            -left-28
            -top-24
            h-64
            w-64
            rounded-full
            bg-blue-500/[0.08]
            blur-3xl
            dark:bg-blue-500/[0.08]
          "
        />

        {/* Purple atmosphere */}
        <div
          className="
            absolute
            -right-24
            top-20
            h-72
            w-72
            rounded-full
            bg-purple-500/[0.08]
            blur-3xl
            dark:bg-purple-500/[0.08]
          "
        />

        {/* Technical grid */}
        <div
          className="
            absolute inset-0
            opacity-[0.018]
            dark:opacity-[0.026]
            [background-image:linear-gradient(to_right,#64748b_1px,transparent_1px),linear-gradient(to_bottom,#64748b_1px,transparent_1px)]
            [background-size:48px_48px]
          "
        />
      </div>

      {/* =========================================================
          HERO CONTAINER
      ========================================================= */}

      <div
        className="
          container
          relative
          mx-auto
          px-5
          pb-9
          pt-[86px]
          sm:px-6
          sm:pb-11
          sm:pt-[88px]
          md:px-10
          md:pb-13
          md:pt-[92px]
          lg:px-12
          lg:pb-14
          lg:pt-[96px]
          xl:px-16
        "
      >
        <div
          className="
            grid
            items-center
            gap-8
            lg:grid-cols-[1.02fr_0.98fr]
            lg:gap-10
            xl:gap-12
          "
        >
          {/* =======================================================
              LEFT CONTENT
          ======================================================= */}

          <motion.div
            initial={
              shouldReduceMotion
                ? { opacity: 1 }
                : { opacity: 0, x: -18 }
            }
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.55,
              ease: "easeOut",
            }}
            className="
              relative
              z-10
              max-w-2xl
            "
          >
            {/* Eyebrow */}
            <div className="mb-4">
              <span
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-blue-200
                  bg-blue-50/80
                  px-3
                  py-1.5
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.14em]
                  text-blue-700
                  dark:border-blue-900/60
                  dark:bg-blue-950/40
                  dark:text-blue-300
                  sm:text-xs
                "
              >
                <span
                  aria-hidden="true"
                  className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-gradient-to-r
                    from-blue-400
                    to-indigo-700
                  "
                />

                Career-Focused IT Training
              </span>
            </div>

            {/* =====================================================
                PRIMARY SEO HEADING
            ===================================================== */}

            <h1
              id="hero-heading"
              className="
                max-w-2xl
                text-[clamp(2.25rem,5vw,4rem)]
                font-extrabold
                leading-[1.03]
                tracking-[-0.035em]
                text-blue-900
                dark:text-white
              "
            >
              Software Training Institute
              <span
                className="
                  block
                  bg-gradient-to-r
                  from-blue-400
                  via-indigo-600
                  to-purple-800
                  bg-clip-text
                  text-transparent
                  dark:from-blue-400
                  dark:via-indigo-400
                  dark:to-purple-400
                "
              >
                in Tirupati
              </span>
            </h1>

            {/* Value Proposition */}
            <p
              className="
                mt-4
                max-w-xl
                text-lg
                font-semibold
                leading-snug
                text-gray-800
                dark:text-gray-100
                sm:text-xl
              "
            >
              Learn practical skills. Build real projects. Grow your career.
            </p>

            {/* Supporting Description */}
            <p
              className="
                mt-3
                max-w-xl
                text-sm
                leading-6
                text-gray-600
                dark:text-gray-300
                sm:text-[15px]
                sm:leading-7
              "
            >
              Build job-ready software and technology skills through
              practical training, real-world projects, expert mentorship,
              and career-focused programs.
            </p>

            {/* =====================================================
                CTA
            ===================================================== */}

            <div
              className="
                mt-6
                flex
                flex-col
                gap-2.5
                sm:flex-row
              "
            >
              {/* Primary CTA */}
              <Link
                to="/courses"
                className="
                  group
                  inline-flex
                  min-h-11
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-gradient-to-r
                  from-indigo-600
                  to-blue-600
                  px-5
                  text-sm
                  font-bold
                  text-white
                  shadow-md
                  shadow-blue-500/15
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:shadow-lg
                  hover:shadow-blue-500/20
                  focus:outline-none
                  focus:ring-2
                  focus:ring-blue-500
                  focus:ring-offset-2
                  dark:focus:ring-offset-gray-950
                  sm:px-6
                "
              >
                Explore IT Courses

                <FaArrowRight
                  aria-hidden="true"
                  className="
                    text-[11px]
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </Link>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/918332029863"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat with IA4Solutions on WhatsApp"
                className="
                  inline-flex
                  min-h-11
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  border
                  border-green-500/70
                  bg-white/90
                  px-5
                  text-sm
                  font-bold
                  text-green-600
                  shadow-sm
                  backdrop-blur-sm
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:bg-green-50
                  hover:shadow-md
                  focus:outline-none
                  focus:ring-2
                  focus:ring-green-500
                  focus:ring-offset-2
                  dark:border-green-500/60
                  dark:bg-gray-900/80
                  dark:text-green-400
                  dark:hover:bg-green-950/30
                  dark:focus:ring-offset-gray-950
                  sm:px-6
                "
              >
                <FaWhatsapp
                  aria-hidden="true"
                  className="text-lg"
                />

                Chat on WhatsApp
              </a>
            </div>

            {/* =====================================================
                TRUST POINTS
            ===================================================== */}

            <div
              className="
                mt-6
                border-t
                border-gray-200
                pt-4
                dark:border-gray-800
              "
            >
              <div
                className="
                  grid
                  grid-cols-2
                  gap-x-4
                  gap-y-2.5
                  sm:flex
                  sm:flex-wrap
                  sm:gap-x-5
                  sm:gap-y-2
                "
              >
                {trustPoints.map((point) => (
                  <div
                    key={point}
                    className="
                      flex
                      items-center
                      gap-2
                      text-xs
                      font-medium
                      text-gray-600
                      dark:text-gray-300
                      sm:text-sm
                    "
                  >
                    <span
                      className="
                        flex
                        h-5
                        w-5
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-emerald-100
                        text-emerald-600
                        dark:bg-emerald-900/30
                        dark:text-emerald-400
                      "
                    >
                      <FaCheck
                        aria-hidden="true"
                        className="text-[8px]"
                      />
                    </span>

                    {point}
                  </div>
                ))}
              </div>
            </div>

            {/* Compact Metadata */}
            <div
              className="
                mt-4
                flex
                flex-wrap
                items-center
                gap-x-3
                gap-y-1.5
                text-[10px]
                font-semibold
                uppercase
                tracking-[0.07em]
                text-gray-400
                dark:text-gray-500
                sm:text-[11px]
              "
            >
              <span>Classroom &amp; Online</span>

              <span
                aria-hidden="true"
                className="
                  h-1
                  w-1
                  rounded-full
                  bg-gray-400
                "
              />

              <span>Tirupati</span>

              <span
                aria-hidden="true"
                className="
                  h-1
                  w-1
                  rounded-full
                  bg-gray-400
                "
              />

              <span>Career-Focused Programs</span>
            </div>
          </motion.div>

          {/* =======================================================
              RIGHT VISUAL
          ======================================================= */}

          <motion.div
            initial={
              shouldReduceMotion
                ? { opacity: 1 }
                : {
                    opacity: 0,
                    x: 18,
                    scale: 0.985,
                  }
            }
            animate={{
              opacity: 1,
              x: 0,
              scale: 1,
            }}
            transition={{
              duration: 0.6,
              delay: 0.06,
              ease: "easeOut",
            }}
            className="
              relative
              mx-auto
              w-full
              max-w-lg
              lg:max-w-xl
            "
          >
            {/* ===================================================
                MAIN IMAGE
            =================================================== */}

            <div
              className="
                relative
                rounded-[1.5rem]
                border
                border-white/70
                bg-white/60
                p-1.5
                shadow-[0_20px_60px_-25px_rgba(30,41,59,0.25)]
                backdrop-blur-lg
                dark:border-gray-700/80
                dark:bg-gray-800/40
                dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.6)]
                sm:p-2
              "
            >
              <div
                className="
                  relative
                  overflow-hidden
                  rounded-[1.2rem]
                  sm:rounded-[1.35rem]
                "
              >
                <img
                  src={heroImg1}
                  alt="Students attending software training at IA4Solutions in Tirupati"
                  width="900"
                  height="1100"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="
                    h-[330px]
                    w-full
                    object-cover
                    object-center
                    sm:h-[410px]
                    md:h-[460px]
                    lg:h-[500px]
                  "
                />

                {/* Image Overlay */}
                <div
                  aria-hidden="true"
                  className="
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-gray-950/70
                    via-gray-950/5
                    to-transparent
                  "
                />

                {/* Status Label */}
                <div
                  className="
                    absolute
                    left-3
                    top-3
                    inline-flex
                    items-center
                    gap-1.5
                    rounded-full
                    border
                    border-white/15
                    bg-black/35
                    px-2.5
                    py-1
                    text-[9px]
                    font-bold
                    uppercase
                    tracking-[0.12em]
                    text-white
                    backdrop-blur-md
                    sm:left-4
                    sm:top-4
                  "
                >
                  <span
                    aria-hidden="true"
                    className="
                      h-1.5
                      w-1.5
                      rounded-full
                      bg-emerald-400
                    "
                  />

                  Learn • Build • Grow
                </div>

                {/* Image Caption */}
                <div
                  className="
                    absolute
                    bottom-0
                    left-0
                    right-0
                    p-3.5
                    sm:p-5
                  "
                >
                  <div className="max-w-md">
                    <div
                      className="
                        flex
                        items-center
                        gap-1.5
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-[0.15em]
                        text-blue-200
                        sm:text-[10px]
                      "
                    >
                      <HiSparkles
                        aria-hidden="true"
                        className="text-xs"
                      />

                      Technology-Focused Learning
                    </div>

                    <p
                      className="
                        mt-1
                        text-base
                        font-bold
                        leading-tight
                        text-white
                        sm:text-lg
                        md:text-xl
                      "
                    >
                      Turn learning into practical
                      career skills.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ===================================================
                LEARNING AREAS
            =================================================== */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? { opacity: 1 }
                  : { opacity: 0, y: 8 }
              }
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.4,
                delay: 0.35,
              }}
              className="
                absolute
                -left-3
                top-7
                hidden
                w-44
                rounded-xl
                border
                border-white/80
                bg-white/95
                p-3
                shadow-lg
                backdrop-blur-xl
                dark:border-gray-700
                dark:bg-gray-900/95
                sm:block
                lg:-left-5
              "
            >
              <div className="flex items-center gap-2">
                <span
                  className="
                    flex
                    h-7
                    w-7
                    shrink-0
                    items-center
                    justify-center
                    rounded-lg
                    bg-indigo-100
                    text-indigo-600
                    dark:bg-indigo-900/40
                    dark:text-indigo-300
                  "
                >
                  <FaLaptopCode
                    aria-hidden="true"
                    className="text-xs"
                  />
                </span>

                <div>
                  <p
                    className="
                      text-[8px]
                      font-bold
                      uppercase
                      tracking-[0.12em]
                      text-gray-400
                      dark:text-gray-500
                    "
                  >
                    Learning Areas
                  </p>

                  <p
                    className="
                      text-[11px]
                      font-bold
                      text-gray-800
                      dark:text-gray-100
                    "
                  >
                    Technology Programs
                  </p>
                </div>
              </div>

              <div className="mt-2.5 space-y-1.5">
                {learningAreas.map((item) => (
                  <div
                    key={item}
                    className="
                      flex
                      items-center
                      gap-2
                      text-[10px]
                      font-semibold
                      text-gray-700
                      dark:text-gray-200
                    "
                  >
                    <span
                      aria-hidden="true"
                      className="
                        h-1
                        w-1
                        shrink-0
                        rounded-full
                        bg-indigo-500
                      "
                    />

                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* ===================================================
                TRAINING APPROACH
            =================================================== */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? { opacity: 1 }
                  : { opacity: 0, y: 8 }
              }
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.4,
                delay: 0.48,
              }}
              className="
                absolute
                -bottom-4
                -right-2
                hidden
                w-48
                rounded-xl
                border
                border-white/80
                bg-white/95
                p-3
                shadow-lg
                backdrop-blur-xl
                dark:border-gray-700
                dark:bg-gray-900/95
                sm:block
                lg:-right-4
              "
            >
              <div className="flex items-center justify-between gap-2">
                <p
                  className="
                    text-[8px]
                    font-bold
                    uppercase
                    tracking-[0.12em]
                    text-gray-400
                    dark:text-gray-500
                  "
                >
                  Training Approach
                </p>

                <span
                  className="
                    rounded-full
                    bg-emerald-100
                    px-1.5
                    py-0.5
                    text-[8px]
                    font-bold
                    text-emerald-600
                    dark:bg-emerald-900/30
                    dark:text-emerald-400
                  "
                >
                  Practical
                </span>
              </div>

              <div className="mt-2.5 space-y-2">
                {trainingApproach.map((item, index) => (
                  <div
                    key={item}
                    className="flex items-center gap-2"
                  >
                    <span
                      className="
                        flex
                        h-5
                        w-5
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-gray-100
                        text-gray-600
                        dark:bg-gray-800
                        dark:text-gray-300
                      "
                    >
                      {index === 0 ? (
                        <HiAcademicCap
                          aria-hidden="true"
                          className="text-[9px]"
                        />
                      ) : index === 1 ? (
                        <HiBriefcase
                          aria-hidden="true"
                          className="text-[9px]"
                        />
                      ) : (
                        <FaCheck
                          aria-hidden="true"
                          className="text-[7px]"
                        />
                      )}
                    </span>

                    <span
                      className="
                        text-[10px]
                        font-semibold
                        text-gray-700
                        dark:text-gray-200
                      "
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* =========================================================
          SUBTLE BOTTOM ACCENT
      ========================================================= */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          bottom-0
          left-1/2
          h-px
          w-1/2
          -translate-x-1/2
          bg-gradient-to-r
          from-transparent
          via-indigo-500/20
          to-transparent
        "
      />
    </section>
  );
};

export default HeroSection;